import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useEventListener } from 'taboola-ultimate-ui';
import { authTokenManager } from '../../taboola-common-frontend-modules/authentication';
import { POST_MESSAGES_TYPES, SESSION_STORAGE_KEYS } from '../consts/iframeParentConstants';

const TaApiContext = createContext();

export const TAApiProvider = ({ children, isMock = false }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [accountId, setAccountId] = useState('');

    const handleMessage = useCallback(
        async event => {
            if (event.data.type === POST_MESSAGES_TYPES.VALIDATION_RESULT) {
                setIsConnected(event.data.isValid);
                return;
            }

            if (isConnected) {
                return;
            }

            if ((!event.data.taboolaToken && !event.data.token) || !event.data.sessionExp) {
                return;
            }

            const tokenData = event.data;

            authTokenManager.setToken(tokenData.taboolaToken || tokenData.token, tokenData.sessionExp);
            setAccountId(tokenData.accountId);
            sessionStorage.setItem(SESSION_STORAGE_KEYS.ACCOUNT_ID, tokenData.accountId);

            if (tokenData.numericAccount) {
                sessionStorage.setItem(SESSION_STORAGE_KEYS.NUMERIC_ACCOUNT, tokenData.numericAccount);
            }

            if (!tokenData.validationBypassed) {
                setIsConnected(true);
            }
        },
        [isConnected]
    );

    useEventListener('message', handleMessage, window);

    useEffect(() => {
        if (isConnected) {
            return;
        }

        const hasToken = !!authTokenManager.getToken();
        const accountId = sessionStorage.getItem(SESSION_STORAGE_KEYS.ACCOUNT_ID);

        if (hasToken && accountId) {
            setAccountId(accountId);
        }
    }, [isConnected, setIsConnected, setAccountId]);

    const value = useMemo(
        () => ({
            isConnected,
            setIsConnected,
            accountId,
        }),
        [isConnected, setIsConnected, accountId]
    );

    if (!isConnected && !isMock) {
        return null;
    }

    return <TaApiContext.Provider value={value}>{children}</TaApiContext.Provider>;
};

export const useTAApi = () => {
    const context = useContext(TaApiContext);
    if (!context) {
        throw new Error('useTAApi must be used within a TAApiProvider');
    }
    return context;
};
